import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import BoxCard from "shared/components/box/BoxCard.js";
import ButtonSelect from "shared/components/buttons/ButtonSelect.js";
import ChartLinePricingHistory from "shared/components/charts/ChartLinePricingHistory.js";
import LoadingSquare from "shared/components/loading/LoadingSquare.js";
import MiscOverallField from "shared/components/misc/MiscOverallField.js";
import MiscPlayerAge from "shared/components/misc/MiscPlayerAge.js";
import MiscPlayerChip from "shared/components/misc/MiscPlayerChip.js";
import PopupInformationPricing from "shared/components/popups/PopupInformationPricing.js";
import { getPlayerPricingHistory, getContracts, getPlayers } from "shared/services/api-assistant.js";
import { getPlayerHistory } from "shared/services/api-mfl.js";
import { analyzePlayerAttributes, getCalculatedOverall, getOverallTag, positions } from "shared/utils/player.js";
import BoxMessage from "shared/components/box/BoxMessage.js";
import ChartScatterPlayerContracts from "shared/components/charts/ChartScatterPlayerContracts.js";
import { copyTextToClipboard } from "shared/utils/clipboard.js";

const PagePlayer = (props) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedView = searchParams.get("view");
  const view = ["profile", "pricing", "contracting"].includes(requestedView) ? requestedView : "profile";
  const setView = (value) => {
    setSearchParams((params) => {
      const nextParams = new URLSearchParams(params);
      nextParams.set("view", value);
      return nextParams;
    });
  };

  const [player, setPlayer] = useState(null);
  const [history, setHistory] = useState(null);
  const [pricings, setPricings] = useState(null);
  const [timeUnit, setTimeUnit] = useState("q");
  const [isLoading, setIsLoading] = useState(false);
  const [contracts, setContracts] = useState(null);
  const [hideZeros, setHideZeros] = useState(true);

  // Fetch player data (replace with your actual API call)
  useEffect(() => {
    setIsLoading(true);
    const num = parseInt(id);
    const result = isNaN(num) ? 0 : num;
    getPlayers({
      handleSuccess: (v) => {
        setPlayer(v.data.getPlayers[0]);

        setIsLoading(false);
      },
      params: {
        id: result,
      },
    });
  }, [id]);

  const fetchPlayerHistory = () => {
    if (!player) return;
    getPlayerHistory({
      handleSuccess: (d) => setHistory(d),
      handleError: (e) => console.log(e),
      id: player.id,
    });
  };

  const fetchContracts = () => {
    if (!player) return;
    setIsLoading(true);
    setContracts(null);

    getContracts({
      handleSuccess: (v) => {
        setIsLoading(false);
        setContracts(v.data.getContracts);
      },
      params: {
        minOvr: player.overall,
        maxOvr: player.overall,
        positions: player.positions[0],
        firstPositionOnly: true,
      },
    });
  };

  const fetchPlayerPricingHistory = () => {
    if (!history || !player) return;
    const ages = history.map((entry) => entry.values.age).filter((v) => v !== undefined);
    const overalls = history.map((entry) => entry.values.overall).filter((v) => v !== undefined);

    if (ages.length && overalls.length) {
      getPlayerPricingHistory({
        handleSuccess: (d) => {
          if (d.data.getPlayerPricingHistory) {
            setPricings(d.data.getPlayerPricingHistory);
          }
        },
        handleError: (e) => console.log(e),
        params: {
          minOverall: Math.min(...overalls),
          maxOverall: Math.max(...overalls),
          minAge: Math.min(...ages),
          maxAge: Math.max(...ages),
          position: player.positions[0],
        },
      });
    }
  };

  const getOVRs = () => {
    if (!player) return [];
    return positions
      .map((pos) => {
        const calcOVR = getCalculatedOverall(player, pos.name);
        return {
          pos: pos.name,
          ovr: calcOVR,
          diff: calcOVR - player.overall,
        };
      })
      .filter((a) => a.ovr > 0)
      .sort((a, b) => b.ovr - a.ovr)
      .slice(0, 3);
  };

  const getDiff = (diff) => {
    if (diff === 0) return;
    if (diff > 0) {
      return <div className="d-inline-block text-info">+{diff}</div>;
    } else {
      return <div className="d-inline-block text-danger">{diff}</div>;
    }
  };

  const getEstimatedPricing = () => {
    if (!pricings || !player || pricings.length === 0) {
      return null;
    }

    const matchingPricings = pricings.filter(
      (pricing) => Number(pricing.age) === Number(player.age) && Number(pricing.overall) === Number(player.overall),
    );

    if (matchingPricings.length === 0) {
      return null;
    }

    return matchingPricings.reduce((latest, pricing) => {
      return new Date(pricing.date) > new Date(latest.date) ? pricing : latest;
    });
  };

  useEffect(() => {
    if (player) {
      fetchPlayerHistory();
    }
  }, [player]);

  useEffect(() => {
    if (history) {
      fetchPlayerPricingHistory();
    }
  }, [history]);

  useEffect(() => {
    if (view === "contracting") {
      fetchContracts();
    }
  }, [view, player]);

  if (!player) {
    return <LoadingSquare />;
  }

  return (
    <div id="PagePlayer" className="d-flex flex-column w-100 h-100">
      <nav className="MenuTop d-flex navbar">
        <h3 className="my-2 ps-5">
          <i className="bi bi-person-badge-fill me-2"></i> {player.firstName} {player.lastName}
        </h3>
      </nav>
      <div className="container container-lg max-width-lg w-100 px-1 px-md-4 py-4">
        <div className="d-flex flex-column flex-sm-row m-1 mb-2">
          <div className="d-flex flex-column flex-basis-200">
            <div className="d-flex justify-content-center mb-3">
              <img
                src={`https://d13e14gtps4iwl.cloudfront.net/players/v2/${player.id}/card.png`}
                style={{ width: "80%", maxWidth: "300px" }}
              />
            </div>

            <div className="my-2">
              <div className="px-2 mb-3 d-flex flex-fill flex-row justify-content-center">
                <button
                  className="btn btn-sm background-mfl text-black me-1"
                  onClick={() => window.open("https://app.playmfl.com/players/" + player.id, "_blank")}
                >
                  MFL<i className="bi bi-caret-right-fill"></i>
                </button>
                <button className="btn btn-link btn-sm" onClick={() => copyTextToClipboard(window.location.href)}>
                  <i className="bi bi-share-fill" />
                </button>
              </div>
            </div>
          </div>

          <div className="d-flex flex-column flex-fill">
            <div className="d-flex flex-column-reverse flex-column flex-md-row align-items-end justify-content-end px-2">
              {pricings && (
                <div className="d-flex flex-row flex-fill">
                  Estimated pricing:&nbsp;
                  <PopupInformationPricing />
                  &nbsp;
                  <span className="text-main">${getEstimatedPricing() ? getEstimatedPricing().price : "--"}</span>
                </div>
              )}

              {view === "profile" && !pricings && (
                <div className="d-flex flex-row flex-fill">
                  <LoadingSquare height={"25px"} />
                </div>
              )}
              <div className="mb-1">
                <ButtonSelect
                  selected={view}
                  onChange={(v) => setView(v)}
                  buttons={{
                    profile: "Profile",
                    pricing: "Pricing",
                    contracting: "Contracting",
                  }}
                />
              </div>
            </div>

            {view === "profile" && (
              <BoxCard
                className="mb-2"
                content={
                  <div className={"d-flex w-100 flex-column mt-2"}>
                    <div className="d-flex flex-column flex-md-row">
                      <div className="d-flex flex-column flex-grow-0 mx-1 px-3 py-2 background-color mb-2">
                        <h6>Profile</h6>
                        <div className="d-flex flex-column">
                          <div>#{player.id}</div>
                          <div>{player.positions.join(", ")}</div>
                          <div className="d-flex flex-row">
                            <MiscPlayerAge age={player.age} retirementYears={player.retirementYears} />
                            <i className="bi bi-rulers mx-1"></i>
                            {player.height}
                          </div>
                        </div>
                      </div>

                      <div className="d-flex flex-column flex-grow-1 mx-1 px-3 py-2 background-color mb-2">
                        <h6>Attributes</h6>
                        <div className="d-flex justify-content-center flex-fill align-content-center">
                          <div className="row w-100">
                            <div className={"col-4 col-sm-2 text-center mb-2 mb-md-0 align-content-center"}>
                              PAC
                              <br />
                              {player.pace ? getOverallTag(player.pace) : "None"}
                            </div>
                            <div className={"col-4 col-sm-2 text-center mb-2 mb-md-0 align-content-center"}>
                              SHO
                              <br />
                              {player.shooting ? getOverallTag(player.shooting) : "None"}
                            </div>
                            <div className={"col-4 col-sm-2 text-center mb-2 mb-md-0 align-content-center"}>
                              PAS
                              <br />
                              {player.passing ? getOverallTag(player.passing) : "None"}
                            </div>
                            <div className={"col-4 col-sm-2 text-center align-content-center"}>
                              DRI
                              <br />
                              {player.dribbling ? getOverallTag(player.dribbling) : "None"}
                            </div>
                            <div className={"col-4 col-sm-2 text-center align-content-center"}>
                              DEF
                              <br />
                              {player.defense ? getOverallTag(player.defense) : "None"}
                            </div>
                            <div className={"col-4 col-sm-2 text-center align-content-center"}>
                              PHY
                              <br />
                              {player.physical ? getOverallTag(player.physical) : "None"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex flex-column flex-grow-1 mx-1 px-3 py-2 background-color">
                      <h6>Characteristics</h6>
                      <div className="d-flex flex-wrap">
                        {analyzePlayerAttributes(player).map((c) => (
                          <MiscPlayerChip info={c} />
                        ))}
                      </div>
                    </div>
                  </div>
                }
              />
            )}

            {view === "pricing" && (
              <BoxCard
                className="mb-2"
                title={"Pricing over career"}
                rowThreshold="sm"
                actions={
                  <button
                    className="btn btn-info btn-small text-white"
                    onClick={() => {
                      navigate(
                        `/tools/player-pricing?overall=${player.overall}&age=${player.age}&position=${player.positions[0]}`,
                      );
                    }}
                  >
                    Player pricing <i className="bi bi-caret-right-fill text-white"></i>
                  </button>
                }
                content={
                  <div className="d-flex flex-fill overflow-hidden ratio ratio-4x3">
                    {pricings ? (
                      <div className="d-flex flex-column">
                        <div className="d-flex flex-fill justify-content-end align-items-center">
                          <ButtonSelect
                            className="d-flex flex-row"
                            buttonClassName="btn btn-small"
                            lastButtonClassName="me-2"
                            selectedClassName="btn-info text-white"
                            unselectedClassName="text-main"
                            buttons={{ w: "W", m: "M", q: "Q", y: "Y" }}
                            selected={timeUnit}
                            onChange={setTimeUnit}
                            disabled={!history || !pricings}
                          />
                        </div>
                        <ChartLinePricingHistory pricings={pricings} history={history} timeUnit={timeUnit} />
                      </div>
                    ) : (
                      <div className="h-100 w-100">
                        <LoadingSquare />
                      </div>
                    )}
                  </div>
                }
              />
            )}

            {view === "profile" && (
              <div className="d-flex flex-column flex-md-row flex-fill">
                <BoxCard
                  className="flex-grow-1 mb-2 p-0"
                  noPadding={true}
                  content={
                    <div className="d-flex flex-grow-1">
                      <MiscOverallField player={player} />
                    </div>
                  }
                />

                <BoxCard
                  className="mb-2 flex-auto flex-none"
                  content={
                    <div className="d-flex flex-row flex-md-column mt-1">
                      {getOVRs().map((o) => (
                        <div className={"d-flex flex-md-column mb-1"}>
                          <div className={"d-inline-block text-center line-height-1"} style={{ width: "40px" }}>
                            {o.pos}
                          </div>
                          <div className={"d-inline-block text-center line-height-1"} style={{ width: "40px" }}>
                            {getOverallTag(o.ovr)}
                            <small>{getDiff(o.diff)}</small>
                          </div>
                        </div>
                      ))}
                    </div>
                  }
                />
              </div>
            )}

            {view === "contracting" && (
              <div className="d-flex flex-column flex-md-row flex-fill">
                <BoxCard
                  className="flex-column flex-md-grow-1 flex-md-shrink-1 flex-md-basis-auto flex-basis-0"
                  title={"Contracts per division"}
                  rowThreshold="sm"
                  actions={
                    <small>
                      Hide zero rates
                      <input
                        type="checkbox"
                        className="ms-1"
                        checked={hideZeros}
                        onChange={() => setHideZeros(!hideZeros)}
                      />
                    </small>
                  }
                  content={
                    <div className="d-flex flex-fill overflow-hidden ratio ratio-4x3">
                      {!contracts && !isLoading ? (
                        <BoxMessage content="No selection" />
                      ) : (
                        <ChartScatterPlayerContracts contracts={contracts} hideZeros={hideZeros} />
                      )}
                    </div>
                  }
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PagePlayer;
